import React, { useState, useEffect, useContext, useRef } from 'react'
import { trackPromise } from 'react-promise-tracker'

import { StyleSeries } from 'highcharts/HighchartsStyleHelper'
import {
    SetupDefaultOptions,
    EnableAllSeries,
    DisableCatSeries,
    FULL_HEIGHT_TWO_INPUT_ROW,
} from 'highcharts/HighchartsHelper'

import { GetIncomeWithEvent } from 'store/ChartApiClient'
import AgeSlider from './components/AgeSlider'
import BreakdownStateRow from '../Components/BreakdownStateRow'
import LoadingWrapper from 'components/LoadingWrapper'
import PresentationContext from '../../PresentationContext'
import GraphSummaryText from '../../components/GraphSummaryText'
import { CurrencyFormatter } from '../../../../../../highcharts/HighchartsHelper'
import HighchartsWrapper from '../../../../../../highcharts/HighchartsWrapper'

function usePrevious(value) {
    const ref = useRef()
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

const IncomeAssetWithEventStep = ({ id, incomeloss }) => {
    const PromiseArea = 'graph-income-with-event'

    const [breakdownStateValue, setBreakdownStateValue] = useState('Gross')
    const [coverageStateValue, setCoverageStateValue] = useState('None')
    const [durationStateValue, setDurationStateValue] = useState('Annual')

    const [groupCoverageHide, setGroupCoverageHide] = useState(false)
    const [groupStdCoverageHide, setGroupStdCoverageHide] = useState(false)
    const [indCoverageHide, setIndCoverageHide] = useState(false)
    const [catCoverageHide, setCatCoverageHide] = useState(true)

    const [options, setOptions] = useState({})
    const [error, setError] = useState(null)

    const {
        slider: {
            marginLeft,
            sliderWidth,
            enabled,
            domain,
            values,
            eventSettings,
            updateDomain,
            updateEventSettings,
            setSliderEnabled,
        },
    } = useContext(PresentationContext)

    const prevCoverageStateValue = usePrevious(coverageStateValue)
    const prevEnabledValue = usePrevious(enabled)

    const LegendClick = (legendName, visible, chart) => {
        if (legendName === 'Group LTD Coverage') {
            setGroupCoverageHide(visible)
            if (chart.series) {
                chart.series.forEach((element) => {
                    if (element.name === 'Group LTD Coverage Prem') {
                        !element.visible ? element.show() : element.hide()
                    }
                })
            }
        }

        if (legendName === 'Group STD Coverage') {
            setGroupStdCoverageHide(visible)
            if (chart.series) {
                chart.series.forEach((element) => {
                    if (element.name === 'Group STD Coverage Prem') {
                        !element.visible ? element.show() : element.hide()
                    }
                })
            }
        }

        if (legendName === 'Current Ind Coverage') {
            setIndCoverageHide(visible)
            if (chart.series) {
                chart.series.forEach((element) => {
                    if (element.name === 'Current Ind Prem') {
                        !element.visible ? element.show() : element.hide()
                    }
                })
            }
        }

        if (
            legendName &&
            legendName.substring(legendName.length - 3, legendName.length) === 'CAT'
        ) {
            setCatCoverageHide(visible)
        }
    }

    useEffect(() => {
        const StyleGraph = (opts) => {
            opts.title.text = incomeloss ? 'Income Loss Scenarios' : opts.title.text
            opts = SetupDefaultOptions(opts, FULL_HEIGHT_TWO_INPUT_ROW, false)

            if (opts.series) {
                opts.series = opts.series.map((p) => StyleSeries(p))

                if (prevEnabledValue !== enabled) {
                    opts.series = EnableAllSeries(opts.series)
                }

                if (prevCoverageStateValue !== coverageStateValue) {
                    opts.series = EnableAllSeries(opts.series)
                }

                if (catCoverageHide) {
                    opts.series = DisableCatSeries(opts.series)
                }
            }

            opts = {
                ...opts,
                plotOptions: {
                    column: {
                        stacking: 'normal',
                    },
                    series: {
                        events: {
                            legendItemClick: (e) => {
                                const {
                                    target: { name, visible, chart },
                                } = e
                                LegendClick(name, visible, chart)
                            },
                        },
                    },
                },
                yAxis: {
                    min: 0,
                },
            }

            opts.tooltip = {
                formatter: function () {
                    const currency = CurrencyFormatter.format(this.y)
                    return '<b>' + this.series.name + '</b>: <b>' + currency + '</b>'
                },
            }

            setOptions(opts)
        }

        let { start, end } = eventSettings
        if (!enabled) {
            if (groupCoverageHide || groupStdCoverageHide || indCoverageHide || !catCoverageHide) {
                //optimizes to single call
                setGroupCoverageHide(false)
                setGroupStdCoverageHide(false)
                setIndCoverageHide(false)
                setCatCoverageHide(true)
                return
            }

            start = null
            end = null
        } else if (prevCoverageStateValue !== coverageStateValue && !catCoverageHide) {
            //optimizes to single call for CAT
            setCatCoverageHide(true)
            return
        }

        trackPromise(
            GetIncomeWithEvent(
                id,
                start,
                end,
                coverageStateValue,
                breakdownStateValue,
                durationStateValue,
                groupCoverageHide,
                groupStdCoverageHide,
                indCoverageHide,
                catCoverageHide
            )
                .then((resp) => {
                    const { data } = resp
                    StyleGraph(data)

                    const {
                        formValues: { ageSlider },
                    } = data

                    updateDomain([ageSlider.sliderMin, ageSlider.sliderMax])
                })
                .catch((err) => {
                    setError(err)
                })
                .finally(() => {}),
            PromiseArea
        )
    }, [
        coverageStateValue,
        breakdownStateValue,
        durationStateValue,
        eventSettings,
        id,
        enabled,
        groupCoverageHide,
        groupStdCoverageHide,
        indCoverageHide,
        catCoverageHide,
    ])

    const sliderChange = (values) => {
        if (enabled) {
            updateEventSettings({
                start: values[0],
                end: values[1],
            })
        }
    }

    if (error) {
        return <p>Something went wrong, try again later</p>
    }

    return (
        <LoadingWrapper area={PromiseArea}>
            <GraphSummaryText options={options} />
            <HighchartsWrapper options={options} />

            <div style={{ display: 'flex', alignItems: 'center' }}>
                {!enabled && <div className="overlay"></div>}
                <div
                    style={{
                        width: marginLeft * 0.85,
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        zIndex: '100',
                    }}
                >
                    <input
                        id="life-event-checkbox"
                        type="checkbox"
                        checked={enabled}
                        onChange={(event) => {
                            const {
                                target: { checked },
                            } = event
                            setSliderEnabled(checked)
                        }}
                    />
                </div>
                <div style={{ width: sliderWidth, paddingLeft: marginLeft * 0.15 }}>
                    <AgeSlider
                        className="mt-4"
                        sliderChange={sliderChange}
                        domain={domain}
                        step={1}
                        values={[eventSettings.start, eventSettings.end]}
                        enabled={enabled}
                    ></AgeSlider>
                </div>
            </div>
            <BreakdownStateRow
                hideDuration
                hideCoverage={incomeloss && coverageStateValue === 'None'}
                breakdownValue={breakdownStateValue}
                coverageValue={coverageStateValue}
                durationValue={durationStateValue}
                breakdownChange={setBreakdownStateValue}
                coverageChange={setCoverageStateValue}
                durationChange={setDurationStateValue}
            />
        </LoadingWrapper>
    )
}

export default IncomeAssetWithEventStep
